import _ from "lodash";
import moment from "moment";
import type {
    FilterKey,
    IApplicationFilterItem,
} from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import type { ApplicationFilters } from "stores/ApplicationFilters/ApplicationFiltersStore";

// Chip rendering needs to answer a different question than "is this filter set?".
// We need to know whether the chip the user currently sees still matches the last
// successfully applied filter snapshot. This helper centralizes that comparison so
// chip components do not each have to understand how to compare compound filters,
// grouped metadata, or order-insensitive array/object values.
type ComparableFilters = Partial<ApplicationFilters> | null | undefined;

function isExtendedMetadataFilterKey(
    filterKey: FilterKey,
): filterKey is `extendedMetadata.${string}` {
    return filterKey.startsWith("extendedMetadata.");
}

// Normalize values before equality checks so semantically equivalent filter values
// compare the same even if MobX/request serialization gives them a different shape.
// Examples: Moment vs Date, array ordering differences, empty strings, and object key order.
function normalizeComparableValue(value: unknown): unknown {
    if (value === undefined || value === null) return undefined;

    if (moment.isMoment(value)) {
        return value.valueOf();
    }

    if (value instanceof Date) {
        return value.getTime();
    }

    if (Array.isArray(value)) {
        const normalizedValues = value
            .map((entry) => normalizeComparableValue(entry))
            .filter((entry) => entry !== undefined);

        if (normalizedValues.length === 0) return undefined;

        return normalizedValues.sort((left, right) =>
            JSON.stringify(left).localeCompare(JSON.stringify(right)),
        );
    }

    if (_.isPlainObject(value)) {
        const normalizedEntries = Object.entries(value).reduce<
            Record<string, unknown>
        >((result, [key, entryValue]) => {
            const normalizedValue = normalizeComparableValue(entryValue);
            if (normalizedValue === undefined) return result;

            result[key] = normalizedValue;
            return result;
        }, {});

        return Object.keys(normalizedEntries).length > 0
            ? Object.keys(normalizedEntries)
                  .sort()
                  .reduce<Record<string, unknown>>((result, key) => {
                      result[key] = normalizedEntries[key];
                      return result;
                  }, {})
            : undefined;
    }

    if (typeof value === "string") {
        const trimmedValue = value.trim();
        return trimmedValue.length > 0 ? trimmedValue : undefined;
    }

    return value;
}

function getDateRangeComparableValue(filters: ComparableFilters) {
    if (!filters?.startDate || !filters?.endDate) return undefined;

    // dateRange is a UI-level chip backed by multiple fields, so compare the
    // combined shape instead of any single property.
    return normalizeComparableValue({
        startDate: filters.startDate,
        endDate: filters.endDate,
        dateRangeLabel: filters.dateRangeLabel,
    });
}

function getCallDurationComparableValue(filters: ComparableFilters) {
    const minCallDuration = filters?.minCallDuration ?? 0;
    const maxCallDuration = filters?.maxCallDuration ?? 0;

    if (minCallDuration === 0 && maxCallDuration === 0) return undefined;

    return {
        minCallDuration,
        maxCallDuration,
    };
}

function getMessageCountComparableValue(filters: ComparableFilters) {
    const minMessageCount = filters?.minMessageCount ?? 0;
    const maxMessageCount = filters?.maxMessageCount ?? 0;

    if (minMessageCount === 0 && maxMessageCount === 0) return undefined;

    return {
        minMessageCount,
        maxMessageCount,
    };
}

function getHierarchyComparableValue(filters: ComparableFilters) {
    if (!filters?.hierarchyIds || filters.hierarchyIds.length === 0) {
        return undefined;
    }

    // Hierarchy chips implicitly reflect the downstream category/subcategory selection.
    // If those diverge from what was last applied, the hierarchy chip should show
    // as not applied too.
    return normalizeComparableValue({
        hierarchyIds: filters.hierarchyIds,
        selectedCategories: filters.selectedCategories,
        selectedSubcategories: filters.selectedSubcategories,
    });
}

function getTopicComparableValue(
    filters: ComparableFilters,
    valueKey: "topics" | "themes" | "primaryTopics" | "drivers",
    modeKey:
        | "containsTopics"
        | "containsThemes"
        | "containsPrimaryTopics"
        | "containsDrivers",
) {
    const value = filters?.[valueKey];
    if (!value || value.length === 0) return undefined;

    return normalizeComparableValue({
        [valueKey]: value,
        [modeKey]: filters?.[modeKey],
    });
}

function getConversationLabelComparableValue(filters: ComparableFilters) {
    if (!filters?.dataLabels || filters.dataLabels.length === 0) {
        return undefined;
    }

    return normalizeComparableValue({
        dataLabels: filters.dataLabels,
        containsDataLabels: filters.containsDataLabels,
        containsResearchLabels: filters.containsResearchLabels,
    });
}

function getExtendedMetadataComparableValue(
    filters: ComparableFilters,
    filterKey: `extendedMetadata.${string}`,
) {
    const metadataKey = filterKey.replace("extendedMetadata.", "");
    if (metadataKey.length === 0) return undefined;

    const groupedValue = filters?.extendedMetadataGrouped?.[metadataKey];
    if (groupedValue?.values?.length) {
        return normalizeComparableValue({
            contains: groupedValue.contains,
            values: groupedValue.values,
        });
    }

    return normalizeComparableValue(filters?.extendedMetadata?.[metadataKey]);
}

// Map a rendered chip key to the exact filter payload that defines whether that
// chip is still applied. This intentionally works at the chip level rather than
// the raw store-key level because some chips are compound representations.
function getComparableFilterValue(
    filters: ComparableFilters,
    filterItem: IApplicationFilterItem,
) {
    const { filterKey } = filterItem;

    if (filterKey === "dateRange") {
        return getDateRangeComparableValue(filters);
    }

    if (filterKey === "callDuration") {
        return getCallDurationComparableValue(filters);
    }

    if (filterKey === "messageCount") {
        return getMessageCountComparableValue(filters);
    }

    if (filterKey === "hierarchyIds") {
        return getHierarchyComparableValue(filters);
    }

    if (filterKey === "topics") {
        return getTopicComparableValue(filters, "topics", "containsTopics");
    }

    if (filterKey === "themes") {
        return getTopicComparableValue(filters, "themes", "containsThemes");
    }

    if (filterKey === "primaryTopics") {
        return getTopicComparableValue(
            filters,
            "primaryTopics",
            "containsPrimaryTopics",
        );
    }

    if (filterKey === "drivers") {
        return getTopicComparableValue(filters, "drivers", "containsDrivers");
    }

    if (filterKey === "dataLabels") {
        return getConversationLabelComparableValue(filters);
    }

    if (isExtendedMetadataFilterKey(filterKey)) {
        return getExtendedMetadataComparableValue(filters, filterKey);
    }

    return normalizeComparableValue(_.get(filters, filterKey));
}

export function getAppliedFilterStateByKey({
    appliedFilters,
    currentFilters,
    filterItems,
}: {
    appliedFilters?: ApplicationFilters | null;
    currentFilters: ApplicationFilters;
    filterItems: IApplicationFilterItem[];
}): Partial<Record<FilterKey, boolean>> {
    if (appliedFilters === undefined) {
        // Undefined means the caller did not supply a committed/applied baseline
        // to compare against. In that case we intentionally keep chips in the
        // default applied state rather than marking them gray.
        return filterItems.reduce<Partial<Record<FilterKey, boolean>>>(
            (result, filterItem) => {
                result[filterItem.filterKey] = true;
                return result;
            },
            {},
        );
    }

    return filterItems.reduce<Partial<Record<FilterKey, boolean>>>(
        (result, filterItem) => {
            result[filterItem.filterKey] = _.isEqual(
                getComparableFilterValue(currentFilters, filterItem),
                getComparableFilterValue(appliedFilters, filterItem),
            );

            return result;
        },
        {},
    );
}
